import { useState, useEffect } from "react";

const WEBINAR_DATE = new Date("2026-04-15T18:00:00Z");

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
  "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function getTimeLeft(target: Date): TimeLeft {
  const diff = Math.max(0, target.getTime() - Date.now());
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

type FormStatus = "idle" | "submitting" | "success" | "error";

export default function WebinarHero() {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(getTimeLeft(WEBINAR_DATE));
  const [formStatus, setFormStatus] = useState<FormStatus>("idle");
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [stateError, setStateError] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setTimeLeft(getTimeLeft(WEBINAR_DATE)), 1000);
    return () => clearInterval(id);
  }, []);

  const pad = (n: number) => String(n).padStart(2, "0");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formStatus === "submitting") return;
    if (selectedStates.length === 0) {
      setStateError(true);
      return;
    }
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) return;
    setFormStatus("submitting");
    const data = new URLSearchParams();
    Array.from(new FormData(form)).forEach(([key, val]) => {
      data.append(key, String(val));
    });
    data.set("states_of_interest", selectedStates.join(", "));
    try {
      const res = await fetch("https://readdy.ai/api/form/d70pc2pqa83q0hmd1er0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus("success");
        setSelectedStates([]);
        setStateError(false);
        form.reset();
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  const countdownItems = [
    { val: pad(timeLeft.days), label: "Days" },
    { val: pad(timeLeft.hours), label: "Hours" },
    { val: pad(timeLeft.minutes), label: "Min" },
    { val: pad(timeLeft.seconds), label: "Sec" },
  ];

  return (
    <section className="bg-white pt-32 pb-10 md:pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">

        {/* ---- Left column ---- */}
        <div className="flex flex-col gap-7">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse flex-shrink-0" />
              <span className="text-xs font-semibold text-primary whitespace-nowrap">Free Live Webinar</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-alt-bg border border-gray-200 rounded-full px-4 py-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-event-line text-navy text-xs" />
              </span>
              <span className="text-xs font-semibold text-navy whitespace-nowrap">April 15, 2026 · 2:00 PM ET</span>
            </div>
          </div>

          <h1
            className="text-navy font-extrabold leading-tight"
            style={{ fontSize: "clamp(32px, 4vw, 52px)", lineHeight: "1.1" }}
          >
            Live Webinar: How Title Companies Win More{" "}
            <span className="text-primary">Closings</span> with AI-Powered Outreach
          </h1>

          <p className="text-[#4a5568] text-base leading-relaxed" style={{ lineHeight: "1.65" }}>
            See how Imperium helps title teams engage agents at the exact moment listings go live, 
            deals go pending, and new closing opportunities are created.
          </p>

          {/* Bullets */}
          <ul className="flex flex-col gap-3">
            {[
              "Real-time listing and pending deal triggers",
              "AI-powered calls to agents and loan officers",
              "Built for title workflows, not generic outreach",
              "Designed to create more conversations and more closings",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary/10 flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-primary text-xs" />
                </span>
                <span className="text-[#4a5568] text-sm leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>

          {/* Event details */}
          <div className="rounded-2xl bg-alt-bg border border-gray-200 px-5 py-5 flex flex-col gap-3">
            {[
              { icon: "ri-calendar-line", label: "Date", value: "Tuesday, April 15, 2026" },
              { icon: "ri-time-line", label: "Time", value: "2:00 PM ET · 11:00 AM PT" },
              { icon: "ri-live-line", label: "Format", value: "Live online webinar + Q&A" },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border border-gray-200 flex-shrink-0 mt-0.5">
                  <i className={`${item.icon} text-navy text-sm`} />
                </span>
                <div className="flex items-start gap-2 min-w-0 flex-wrap">
                  <span className="text-xs font-semibold text-[#4a5568] whitespace-nowrap mt-0.5">{item.label}:</span>
                  <span className="text-sm font-medium text-navy leading-snug">{item.value}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Countdown */}
          <div>
            <p className="text-xs font-semibold text-[#4a5568] uppercase tracking-wide mb-2 sm:mb-3">Webinar starts in</p>
            <div className="flex items-center gap-2 sm:gap-3">
              {countdownItems.map((item, i) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div className="bg-navy text-white font-mono font-bold text-lg sm:text-xl rounded-xl w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
                    {item.val}
                  </div>
                  <span className="text-xs text-[#4a5568] font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Registration Form */}
          <div id="webinar-form" className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 mt-2" style={{ boxShadow: "0 4px 24px rgba(11,31,51,0.07)" }}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
              <h2 className="text-navy font-bold text-lg">Register for the Webinar</h2>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-xs font-semibold text-green-600 whitespace-nowrap">Spots Available</span>
              </div>
            </div>

            {formStatus === "success" ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-green-500 text-2xl" />
                </div>
                <h3 className="text-navy font-bold text-base mb-2">You&apos;re Registered!</h3>
                <p className="text-[#4a5568] text-sm leading-relaxed">
                  Check your inbox for confirmation details. We&apos;ll send you the link and reminders before the webinar.
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="webinar-registration"
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
              >
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-navy" htmlFor="firstName">First Name *</label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      required
                      placeholder="Jane"
                      className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-navy placeholder-gray-400 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy/20 transition-colors bg-white"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-navy" htmlFor="lastName">Last Name *</label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      required
                      placeholder="Smith"
                      className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-navy placeholder-gray-400 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy/20 transition-colors bg-white"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-navy" htmlFor="email">Work Email *</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="jane@titlecompany.com"
                    className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-navy placeholder-gray-400 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy/20 transition-colors bg-white"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-navy" htmlFor="company">Company *</label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    required
                    placeholder="Premier Title Group"
                    className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-navy placeholder-gray-400 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy/20 transition-colors bg-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-navy" htmlFor="phone">
                      Phone <span className="text-[#4a5568] font-normal">(opt.)</span>
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="(555) 000-0000"
                      className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-navy placeholder-gray-400 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy/20 transition-colors bg-white"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-navy" htmlFor="role">
                      Your Role
                    </label>
                    <input
                      id="role"
                      name="role"
                      type="text"
                      required
                      placeholder="e.g. Escrow Officer"
                      className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-navy placeholder-gray-400 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy/20 transition-colors bg-white"
                    />
                  </div>
                </div>

                {/* Outreach Strategy Interest */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-navy" htmlFor="outreach_strategy_interest">
                    Outreach Strategy Interest
                  </label>
                  <select
                    id="outreach_strategy_interest"
                    name="outreach_strategy_interest"
                    required
                    defaultValue=""
                    className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy/20 transition-colors bg-white cursor-pointer"
                  >
                    <option value="" disabled>Select your strategy...</option>
                    <option value="Tier 1: Outbound Engine (Your Data)">Tier 1: Outbound Engine — Your Data</option>
                    <option value="Tier 2: Market-Triggered Outreach">Tier 2: Market-Triggered Outreach (Most Popular)</option>
                    <option value="Tier 3: Market Expansion">Tier 3: Market Expansion</option>
                    <option value="Not Sure">Not Sure</option>
                  </select>
                </div>

                {/* States of Interest */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-navy">
                      States of Interest
                    </label>
                    {selectedStates.length > 0 && (
                      <span className="text-xs font-semibold text-primary">
                        {selectedStates.length} selected
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#4a5568]">
                    Select every state where you operate or want to expand.
                  </p>
                  <div className="grid grid-cols-5 sm:grid-cols-6 gap-1.5">
                    {US_STATES.map((abbr) => {
                      const active = selectedStates.includes(abbr);
                      return (
                        <button
                          key={abbr}
                          type="button"
                          onClick={() => {
                            setStateError(false);
                            setSelectedStates((prev) =>
                              prev.includes(abbr)
                                ? prev.filter((s) => s !== abbr)
                                : [...prev, abbr]
                            );
                          }}
                          className={`py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap border ${
                            active
                              ? "bg-navy text-white border-navy"
                              : "bg-white text-[#4a5568] border-gray-200 hover:border-navy hover:text-navy"
                          }`}
                        >
                          {abbr}
                        </button>
                      );
                    })}
                  </div>
                  {stateError && (
                    <p className="text-xs text-red-500">Please select at least one state.</p>
                  )}
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => { setSelectedStates([...US_STATES]); setStateError(false); }}
                      className="text-xs text-primary hover:underline cursor-pointer whitespace-nowrap"
                    >
                      Select all
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={() => setSelectedStates([])}
                      className="text-xs text-[#4a5568] hover:text-navy hover:underline cursor-pointer whitespace-nowrap"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                {formStatus === "error" && (
                  <p className="text-xs text-red-500 leading-relaxed">
                    Something went wrong. Please try again or refresh the page.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={formStatus === "submitting"}
                  className="w-full bg-primary hover:bg-orange-600 text-white font-semibold text-sm py-3.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {formStatus === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Register for the Webinar
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-sm" />
                      </span>
                    </>
                  )}
                </button>

                <p className="text-center text-xs text-[#4a5568]">
                  Limited attendance for live Q&A &nbsp;·&nbsp;{" "}
                  <a href="#demo" className="text-secondary hover:underline cursor-pointer whitespace-nowrap">
                    Prefer to talk now? Hear a real demo call
                  </a>
                </p>
              </form>
            )}
          </div>
        </div>

        {/* ---- Right column: visual — hidden on mobile ---- */}
        <div className="hidden lg:relative lg:flex items-start justify-center pt-4">
          <div
            className="absolute inset-0 rounded-3xl opacity-50"
            style={{ background: "radial-gradient(ellipse at 60% 30%, #f1663a18 0%, transparent 65%)" }}
          />

          <div className="relative z-10 w-full max-w-sm mx-auto">
            {/* Dashboard UI card */}
            <div
              className="bg-navy rounded-2xl overflow-hidden"
              style={{ boxShadow: "0 40px 80px rgba(11,31,51,0.2), 0 10px 30px rgba(241,102,58,0.1)" }}
            >
              {/* Header bar */}
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400/70" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400/70" />
                    <div className="w-3 h-3 rounded-full bg-green-400/70" />
                  </div>
                </div>
                <span className="text-white/40 text-xs font-mono">Imperium · Live Dashboard</span>
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              </div>

              <div className="p-5 flex flex-col gap-4">
                {/* Live call card */}
                <div className="bg-[#162d47] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-white/60 uppercase tracking-wide">AI Call in Progress</span>
                    <div className="flex items-center gap-1.5 bg-green-500/20 border border-green-500/30 rounded-full px-2.5 py-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                      <span className="text-green-300 text-[10px] font-semibold">LIVE</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-secondary to-blue-300 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      MA
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm leading-none mb-0.5">Mike Alvarez</p>
                      <p className="text-white/50 text-xs">Listing Agent · Phoenix, AZ</p>
                    </div>
                    <div className="ml-auto text-white/40 text-xs font-mono">1:12</div>
                  </div>
                  {/* Waveform */}
                  <div className="flex items-end gap-0.5 h-8">
                    {[3,6,9,5,11,8,4,10,7,3,9,6,11,4,8,5,10,7,3,6,9,5,11,8].map((h, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-sm bg-secondary/40"
                        style={{ height: `${h * 2.2}px`, animation: `pulse ${0.4 + i * 0.05}s ease-in-out infinite alternate` }}
                      />
                    ))}
                  </div>
                </div>

                {/* Trigger event */}
                <div className="bg-[#162d47] rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 flex items-center justify-center bg-primary/20 rounded-lg">
                      <i className="ri-home-4-line text-primary text-xs" />
                    </span>
                    <span className="text-white font-semibold text-xs">New Listing Detected</span>
                    <span className="ml-auto text-white/30 text-[10px]">2s ago</span>
                  </div>
                  <p className="text-white/60 text-xs leading-relaxed">
                    3841 Maple Ridge Dr, Scottsdale AZ · $624,000
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: "82%" }} />
                    </div>
                    <span className="text-[10px] text-white/40">Outreach queued</span>
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "Calls Today", value: "147", color: "#19a2fd" },
                    { label: "Conversations", value: "31", color: "#f1663a" },
                    { label: "Closings Routed", value: "6", color: "#22c55e" },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-[#162d47] rounded-xl px-3 py-3 text-center">
                      <p className="font-bold text-lg leading-none mb-0.5" style={{ color: stat.color }}>{stat.value}</p>
                      <p className="text-white/40 text-[10px] leading-tight">{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Pending deal */}
                <div className="bg-[#162d47] rounded-xl p-4 flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-[#19a2fd]/20 rounded-lg flex-shrink-0">
                    <i className="ri-file-list-3-line text-secondary text-sm" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-xs leading-none mb-0.5">Pending Closing Detected</p>
                    <p className="text-white/50 text-[10px] truncate">$485,000 · Cooperating agent identified</p>
                  </div>
                  <div className="flex-shrink-0 text-[10px] font-semibold text-[#19a2fd] whitespace-nowrap">Outreach sent</div>
                </div>
              </div>
            </div>

            {/* Floating badges */}
            <div
              className="absolute -left-10 top-16 bg-white rounded-xl px-3.5 py-2.5 flex items-center gap-2.5"
              style={{ boxShadow: "0 8px 32px rgba(11,31,51,0.12)" }}
            >
              <div className="w-7 h-7 flex items-center justify-center bg-orange-50 rounded-lg flex-shrink-0">
                <i className="ri-flashlight-line text-primary text-sm" />
              </div>
              <div>
                <p className="text-navy text-[11px] font-bold leading-tight">Trigger Fired</p>
                <p className="text-[#4a5568] text-[10px]">Listing went live 4s ago</p>
              </div>
            </div>

            <div
              className="absolute -right-8 top-40 bg-white rounded-xl px-3.5 py-2.5 flex items-center gap-2.5"
              style={{ boxShadow: "0 8px 32px rgba(11,31,51,0.12)" }}
            >
              <div className="w-7 h-7 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0">
                <i className="ri-check-double-line text-green-500 text-sm" />
              </div>
              <div>
                <p className="text-navy text-[11px] font-bold leading-tight">Closing Won</p>
                <p className="text-[#4a5568] text-[10px]">Routed to closer</p>
              </div>
            </div>

            <div
              className="absolute -left-8 bottom-10 bg-white rounded-xl px-3.5 py-2.5 flex items-center gap-2.5"
              style={{ boxShadow: "0 8px 32px rgba(11,31,51,0.12)" }}
            >
              <div className="w-7 h-7 flex items-center justify-center bg-blue-50 rounded-lg flex-shrink-0">
                <i className="ri-shield-check-line text-secondary text-sm" />
              </div>
              <div>
                <p className="text-navy text-[11px] font-bold leading-tight">SOC 2 Certified</p>
                <p className="text-[#4a5568] text-[10px]">Secure by default</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
